import { differenceInDays, format, formatISO9075, formatRelative, isValid, parseISO } from 'date-fns'
import locale from 'date-fns/locale/en-AU'

/**
 *
 * @param value Date | String value
 * @returns UTC date format--> "2024-01-24"
 */ export const toUTCDate = (fromDate: Date | string): string => {
  {
    var date = new Date(fromDate)
    return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().split('T')[0]
  }
}

/**
 * Convert date to date string
 * @param value Date | String value
 * @returns UTC date format--> "30 Jan 24"
 */ export const toFriendlyDate = (fromDate: Date | string): string => {
  {
    var date = new Date(fromDate)
    var dateAdjusted = new Date(date.getTime() - date.getTimezoneOffset() * 60000)
    return format(dateAdjusted, 'dd MMM yy')
  }
}

/**
 * Convert date to time string
 * @param value Date | String value
 * @returns UTC date format--> "12:10:30"
 */ export const toJustTime = (fromDate: Date | string): string => {
  {
    var date = new Date(fromDate)
    var dateAdjusted = new Date(date.getTime() - date.getTimezoneOffset() * 60000)
    return format(dateAdjusted, 'hh:mm:ss')
  }
}

export const convertUTCToDate = (date: Date | string) => {
  {
    // Discard the time component so date-only fields are not shifted by the browser timezone.
    const dateOnly = typeof date === 'string' ? date.slice(0, 10) : date.toISOString().slice(0, 10)
    const splittedDate = dateOnly.split('-')
    const year = Number(splittedDate?.[0])
    const month = Number(splittedDate?.[1])
    const day = Number(splittedDate?.[2])
    return new Date(year, month - 1, day) // the month is 0-indexed
  }
}

/**
 * Convert UTC to Date
 * @param value Date | String value format --> 23/05/2029
 * @returns UTC date format--> Tue May 27 2025 00:00:00 GMT+xxxx (Your Local Timezone)
 */
export const convertDateToUTCSplitWithSlash = (date: string) => {
  {
    const splittedDate = date?.split('/')
    const year = Number(splittedDate?.[2])
    const month = Number(splittedDate?.[1])
    const day = Number(splittedDate?.[0])
    return new Date(year, month - 1, day) // the month is 0-indexed
  }
}

/**
 * Returns today's date set to midnight (00:00:00.000) in the local time zone.
 * @returns {Date}
 */
export const getTodayMidnight = () => {
  return new Date(new Date().setHours(0, 0, 0, 0))
}

/**
 * Return the future date based on today's date
 * @returns dd/mm/yyyy
 */
export const yearsLaterFromToday = (year: number) => {
  const today = new Date()
  today.setUTCFullYear(today.getUTCFullYear() + year)
  today.toISOString()
  return today.toLocaleDateString('en-GB')
}

/**
 *
 * @param value 'string | number | Date | null | undefined' value
 * @returns string value -  "day Month year (30 Apr 24)"
 */
export const toCompactDate = (value: string | number | Date | null | undefined) =>
  value ? format(new Date(value), 'd MMM yy') : ''

/**
 *
 * @param value 'string | number | Date | null | undefined' value
 * @returns string value -  "day Month year (30 Apr 2024)"
 */
export const toShortDate = (value: string | number | Date | null | undefined) =>
  value ? format(new Date(value), 'd MMM yyyy') : ''

/**
 *
 * @param value 'string | number | Date | null | undefined' value
 * @returns string value -  "Day of the week, Month day year (Monday, April 30th 2024)"
 */
export const toLongDate = (value: string | number | Date | null | undefined) =>
  value ? format(new Date(value), 'PPPP') : ''

/**
 *
 * @param value 'string | number | Date' value
 * @returns string value -  "Day/Month/Year (30/04/2024)"
 */
export const toRelativeDate = (value: string | number | Date) => formatRelative(new Date(value), new Date(), { locale })

/**
 *
 * @param value 'string | number | Date' value
 * @returns string value -  "Day-Month-Year (30-04-2024)"
 */
export const toISO8601Date = (value: string | number | Date) =>
  formatISO9075(new Date(value), { representation: 'date' })

export const toReadableTimestamp = (value: string | number | Date | null | undefined) =>
  value ? format(new Date(value), 'd MMM, yyyy h:mm:ss a') : ''

export function getEarliestDate(dateA: string | Date | null, dateB: string | Date | null): Date | null {
  if (!dateA && !dateB) return null
  if (!dateA) return new Date(dateB!)
  if (!dateB) return new Date(dateA)

  const parsedA = new Date(dateA)
  const parsedB = new Date(dateB)

  return parsedA <= parsedB ? parsedA : parsedB
}

export function isWithinDays(date: string | Date | null, days: number): boolean {
  if (!date) return false

  const parsedDate = typeof date === 'string' ? parseISO(date) : date

  if (!isValid(parsedDate)) return false

  const daysUntil = differenceInDays(parsedDate, new Date())

  return daysUntil >= 0 && daysUntil <= days
}
